// AttendanceAnalytics.jsx
// Required dependencies:
// npm install react-chartjs-2 chart.js react-datepicker react-loading-skeleton react-icons tailwindcss
// Configure Tailwind CSS with Roboto and Poppins fonts
// Import 'react-datepicker/dist/react-datepicker.css' in your main file

import React, { useState, useEffect } from 'react';
import { Bar, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { FaFileExcel, FaFilePdf, FaSpinner } from 'react-icons/fa';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

const AttendanceAnalytics = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [dateRange, setDateRange] = useState([new Date(), new Date()]);
  const [startDate, endDate] = dateRange;
  const [data, setData] = useState({ employees: [], attendanceData: {}, hoursData: {}, compliance: 85 });

  useEffect(() => {
    // Simulate data loading
    setIsLoading(true);
    setTimeout(() => {
      setData({
        employees: [
          { id: 1, name: 'John Doe', punch: 90, gps: 85, status: 'Compliant' },
          { id: 2, name: 'Jane Smith', punch: 95, gps: 95, status: 'Compliant' },
          { id: 3, name: 'Bob Johnson', punch: 70, gps: 65, status: 'Non-Compliant' },
          { id: 4, name: 'Alice Brown', punch: 85, gps: 80, status: 'Compliant' },
        ],
        attendanceData: {
          labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
          punch: [20, 25, 22, 28, 24],
          gps: [18, 23, 20, 25, 22],
        },
        hoursData: {
          labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
          field: [30, 35, 32, 38, 34],
          office: [40, 45, 42, 48, 44],
        },
        compliance: 85,
      });
      setIsLoading(false);
    }, 2000);
  }, [dateRange]);

  const barData = {
    labels: data.attendanceData.labels || [],
    datasets: [
      {
        label: 'Attendance Punch',
        data: data.attendanceData.punch || [],
        backgroundColor: '#0EA5E9', // vibrant blue
      },
      {
        label: 'GPS Presence',
        data: data.attendanceData.gps || [],
        backgroundColor: '#14B8A6', // teal
      },
    ],
  };

  const lineData = {
    labels: data.hoursData.labels || [],
    datasets: [
      {
        label: 'Field Hours',
        data: data.hoursData.field || [],
        borderColor: '#8B5CF6', // purple
        backgroundColor: 'rgba(139, 92, 246, 0.2)',
        fill: true,
      },
      {
        label: 'Office Hours',
        data: data.hoursData.office || [],
        borderColor: '#10B981', // soft green
        backgroundColor: 'rgba(16, 185, 129, 0.2)',
        fill: true,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: { position: 'top' },
      tooltip: { enabled: true, mode: 'index', intersect: false },
    },
    animation: { duration: 1000, easing: 'easeOutQuart' },
  };

  const handleExport = (type) => {
    // Simulate export (in real app, use xlsx or jspdf)
    console.log(`Exporting to ${type}`);
  };

  const statusColors = {
    Compliant: 'bg-green-500 text-white',
    'Non-Compliant': 'bg-purple-500 text-white',
  };

  return (
    <div className="p-6 bg-white min-h-screen font-roboto text-gray-800">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-poppins font-bold">Attendance Analytics</h1>
        <div className="flex items-center space-x-4">
          <DatePicker
            selectsRange={true}
            startDate={startDate}
            endDate={endDate}
            onChange={(update) => setDateRange(update)}
            className="border border-gray-300 rounded px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholderText="Select date range"
          />
          <button
            onClick={() => handleExport('Excel')}
            className="bg-blue-500 text-white text-sm px-4 py-1 rounded-full hover:bg-blue-600 transition duration-200 shadow-sm flex items-center space-x-1"
          >
            <FaFileExcel />
            <span>Excel</span>
          </button>
          <button
            onClick={() => handleExport('PDF')}
            className="bg-teal-500 text-white text-sm px-4 py-1 rounded-full hover:bg-teal-600 transition duration-200 shadow-sm flex items-center space-x-1"
          >
            <FaFilePdf />
            <span>PDF</span>
          </button>
        </div>
      </div>

      <hr className="mb-6 border-gray-200" />

      {/* Charts Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <div className="p-4 border border-gray-200 rounded-lg shadow-sm">
          <h2 className="text-lg font-poppins font-semibold mb-2">Attendance Punch vs GPS Presence</h2>
          {isLoading ? (
            <Skeleton height={250} />
          ) : (
            <Bar data={barData} options={chartOptions} />
          )}
        </div>
        <div className="p-4 border border-gray-200 rounded-lg shadow-sm">
          <h2 className="text-lg font-poppins font-semibold mb-2">Field Hours vs Office Hours</h2>
          {isLoading ? (
            <Skeleton height={250} />
          ) : (
            <Line data={lineData} options={chartOptions} />
          )}
        </div>
      </div>

      {/* Compliance Progress Bars */}
      <div className="mb-8">
        <h2 className="text-lg font-poppins font-semibold mb-2">Attendance Compliance</h2>
        <div className="border border-gray-200 rounded-lg p-4 shadow-sm">
          {isLoading ? (
            <Skeleton height={20} />
          ) : (
            <div>
              <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2">
                <div
                  className="bg-green-500 h-2.5 rounded-full"
                  style={{ width: `${data.compliance}%` }}
                ></div>
              </div>
              <p className="text-sm text-gray-600">{data.compliance}% Compliant</p>
            </div>
          )}
        </div>
      </div>

      <hr className="mb-6 border-gray-200" />

      {/* Table Section */}
      <div>
        <h2 className="text-lg font-poppins font-semibold mb-2">Employee-wise Attendance</h2>
        <div className="overflow-x-auto">
          {isLoading ? (
            <Skeleton count={5} height={30} />
          ) : (
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 px-4 text-sm font-poppins font-semibold text-gray-600">Name</th>
                  <th className="text-left py-2 px-4 text-sm font-poppins font-semibold text-gray-600">Punch (%)</th>
                  <th className="text-left py-2 px-4 text-sm font-poppins font-semibold text-gray-600">GPS (%)</th>
                  <th className="text-left py-2 px-4 text-sm font-poppins font-semibold text-gray-600">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.employees.map((emp) => (
                  <tr key={emp.id} className="border-b border-gray-200 hover:bg-gray-50 transition duration-200">
                    <td className="py-2 px-4">{emp.name}</td>
                    <td className="py-2 px-4">{emp.punch}%</td>
                    <td className="py-2 px-4">{emp.gps}%</td>
                    <td className="py-2 px-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${statusColors[emp.status]}`}>
                        {emp.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default AttendanceAnalytics;